<template>
  <v-container
    grid-list-xs
    fluid
    class="pa-0"
  >
    <AnytimeConsultationSearchConditionsWidget
      :search-detail-dialog="searchDetailDialog"
      :option-dialog="optionDialog"
      :has-pediatric-department="hasPediatricDepartment"
      :department-type-options="departmentTypeOptions"
      :child-gender-options="childGenderOptions"
      :department-types="departmentTypes"
      :answer-keywords="answerKeywords"
      :icd10-keyword="icd10Keyword"
      :child-age-slider="childAgeSlider"
      :child-genders="childGenders"
      :doctor-family-name="doctorFamilyName"
      :doctor-first-name="doctorFirstName"
      :doctor-id="doctorId"
      :current-doctor="currentDoctor"
      :is-operator-doctor="isOperatorDoctor"
      :exclude-mode="excludeMode"
      :display-query="displayQuery"
      :display-option="displayOption"
      @search="updateList"
      @update:previous-search-query="previousSearchQuery = $event"
      @update:search-detail-dialog="searchDetailDialog = $event"
      @update:option-dialog="optionDialog = $event"
      @update:department-types="departmentTypes = $event"
      @update:answer-keywords="answerKeywords = $event"
      @update:icd10-keyword="icd10Keyword = $event"
      @update:child-age-slider="childAgeSlider = $event"
      @update:child-genders="childGenders = $event"
      @update:doctor-family-name="doctorFamilyName = $event"
      @update:doctor-first-name="doctorFirstName = $event"
      @update:doctor-id="doctorId = $event"
      @update:exclude-mode="excludeMode = $event"
    />

    <div
      v-if="previousKeys !== null"
      class="results"
    >
      <!-- エラー表示 -->
      <div
        v-if="elasticsearchErrorDetail.length > 0"
        class="pa-6"
      >
        <div class="elasticsearch-error-detail">
          {{ elasticsearchErrorDetail }}
        </div>
      </div>

      <!-- ローディング表示 -->
      <div
        v-else-if="isSearching"
        class="d-flex flex-column align-center justify-center pa-6"
      >
        <v-progress-circular
          indeterminate
          color="grey"
          size="64"
        />
      </div>

      <!-- 結果表示（ローディング中は非表示） -->
      <div v-else>
        <div class="search-keys pa-3">
          <b>検索条件</b><br>
          相談員種別: {{ formatDepartmentTypes(previousKeys.department_types) }}<br>
          暫定診断キーワード: {{ previousKeys.icd10_keyword }}<br>
          回答者: (姓: {{ previousKeys.doctor_family_name }}, 名: {{ previousKeys.doctor_first_name }})<br>
          回答検索キーワード: {{ previousKeys.answer_keywords.join(" ") }}<br>
          追加条件: {{ displayQuery }}<br>
          <span v-if="isOperatorDoctor">
            管理者向け: {{ displayOption }}<br>
          </span>
        </div>

        <div
          class="mt-4"
        >
          新しい順に表示 {{ anytimeConsultations.length }}件
          <span
            v-if="excludedCount > 0"
            class="ml-1 text-red-darken-2 font-weight-bold"
          >
            (全件のうち{{ excludedCount }}件は除外中)
          </span>
        </div>

        <BulkExcludeToolbar
          v-if="isOperatorDoctor"
          :bulk-toolbar-open="bulkToolbarOpen"
          :anytime-consultations="anytimeConsultations"
          :selected-ids="selectedBulkIds"
          :search-query="previousSearchQuery"
          :selection-reset-key="searchResultSessionId"
          @update:bulk-toolbar-open="bulkToolbarOpen = $event"
          @toggle-all-selection="toggleAllBulkSelection"
          @clear-selection="clearBulkSelection"
          @refresh-results="refreshSearchResults"
          @show-toast="showToast"
        />

        <div
          v-else
          class="text-caption text-grey-darken-1"
        >
          ※古い情報やリンク切れを防ぐため、一部の回答を表示対象外としています。
        </div>

        <AnytimeConsultationSearchResults
          class="mt-5"
          :anytime-consultations="anytimeConsultations"
          :total-count="totalCount"
          :excluded-count="excludedCount"
          :selection-reset-key="searchResultSessionId"
          :previous-keys="previousKeys"
          :bulk-toolbar-open="bulkToolbarOpen"
          :selected-ids="selectedBulkIds"
          @toggle-selection="onToggleBulkSelection"
        />

      </div>

      <v-snackbar
        v-model="snackbar"
        :timeout="3000"
        :color="snackbarColor"
        bottom
        right
      >
        {{ snackbarMessage }}
      </v-snackbar>
    </div>
  </v-container>
</template>

<script>
import anytimeConsultationApi from "../../../../api/anytime_consultations";
import AnytimeConsultationSearchResults from "../../../../widgets/anytime-consultation-answered-search/ui/AnytimeConsultationSearchResults.vue";
import BulkExcludeToolbar from "../../../../widgets/anytime-consultation-answered-search/ui/BulkExcludeToolbar.vue";
import AnytimeConsultationSearchConditionsWidget from "../../../../widgets/anytime-consultation-answered-search";
import { syncDoctorNameByDoctorId } from "../../../../shared/utils/doctorFilter";
import {
  DEPARTMENT_TYPE_OPTIONS,
  CHILD_GENDER_OPTIONS,
  buildSearchKeys,
  buildAnsweredSearchRequestQuery,
  normalizeChildAgeSlider,
  buildDisplayQuery,
  buildDisplayOption,
  formatDepartmentTypes,
  persistSearchKeys,
  readPersistedSearchKeys,
} from "../../../../features/anytime-consultation-answered-search";

export default {
  name: "AnytimeConsultationAnsweredSearchPage",
  components: {
    AnytimeConsultationSearchResults,
    BulkExcludeToolbar,
    AnytimeConsultationSearchConditionsWidget,
  },

  beforeRouteLeave(to, from, next) {
    if (to.name === "anytimeConsultation") {
      persistSearchKeys(this.previousKeys);
    } else {
      persistSearchKeys(null);
    }
    next();
  },

  data: () => ({
    // --- 選択肢（定数からインポート）---
    departmentTypeOptions: DEPARTMENT_TYPE_OPTIONS,
    childGenderOptions: CHILD_GENDER_OPTIONS,

    // --- 検索フォーム状態 ---
    departmentTypes: [1, 2, 3],
    answerKeywords: "",
    icd10Keyword: "",
    childAgeSlider: [0, 35],
    childGenders: [],
    doctorFamilyName: "",
    doctorFirstName: "",
    doctorId: null,
    excludeMode: "without_exclude",
    searchDetailDialog: false,
    optionDialog: false,

    // --- 検索結果状態 ---
    previousKeys: null,
    anytimeConsultations: [],
    totalCount: 0,
    excludedCount: 0,
    elasticsearchErrorDetail: "",
    isSearching: false,
    lastSearchRequestBase: null,
    previousSearchQuery: null,

    // --- 一括操作状態 ---
    bulkToolbarOpen: false,
    selectedBulkIds: [],
    searchResultSessionId: 0,

    // --- トースト表示状態 ---
    snackbar: false,
    snackbarMessage: "",
    snackbarColor: "success",
  }),

  computed: {
    currentDoctor() {
      return this.$store.getters.currentDoctor;
    },
    hasAllCapabilities() {
      return this.$store.getters.hasAllCapabilities;
    },
    splitKeywords() {
      return this.answerKeywords.split(/[\s\u3000]+/).filter((v) => v);
    },
    isOperatorDoctor() {
      return this.hasAllCapabilities(["operator:access"]);
    },
    hasPediatricDepartment() {
      return this.departmentTypes.includes(1);
    },
    displayQuery() {
      return buildDisplayQuery({
        doctorId: this.doctorId,
        doctorFamilyName: this.doctorFamilyName,
        doctorFirstName: this.doctorFirstName,
        hasPediatricDepartment: this.hasPediatricDepartment,
        childAgeSlider: this.childAgeSlider,
        childGenders: this.childGenders,
      });
    },
    displayOption() {
      return buildDisplayOption({
        isOperatorDoctor: this.isOperatorDoctor,
        excludeMode: this.excludeMode,
      });
    },
    answeredSearchRequestBase() {
      if (!this.previousKeys) return null;

      return {
        query: buildAnsweredSearchRequestQuery(this.previousKeys),
      };
    },
  },

  watch: {
    doctorId(doctorId) {
      syncDoctorNameByDoctorId({
        doctorId,
        currentDoctor: this.currentDoctor,
        setFamilyName: (v) => { this.doctorFamilyName = v; },
        setFirstName: (v) => { this.doctorFirstName = v; },
      });
    },
    currentDoctor() {
      // doctorId が選択済みのまま currentDoctor が後から解決された場合にも同期
      if (this.doctorId == null) return;
      syncDoctorNameByDoctorId({
        doctorId: this.doctorId,
        currentDoctor: this.currentDoctor,
        setFamilyName: (v) => { this.doctorFamilyName = v; },
        setFirstName: (v) => { this.doctorFirstName = v; },
      });
    },
  },

  created() {
    const persisted = readPersistedSearchKeys();
    if (!persisted) return;

    // 検索条件のみ復元し、検索ボタン押下までは検索を実行しない
    this.previousKeys = null;
    this.departmentTypes = persisted.department_types || [1, 2, 3];
    this.icd10Keyword = persisted.icd10_keyword || "";
    this.doctorFamilyName = persisted.doctor_family_name || "";
    this.doctorFirstName = persisted.doctor_first_name || "";
    this.answerKeywords = (persisted.answer_keywords || []).join(" ");
    this.childGenders = persisted.child_genders || [];
    this.childAgeSlider = normalizeChildAgeSlider(persisted.child_age_slider);

    if (persisted.doctor_id != null) {
      this.doctorId = persisted.doctor_id;
    } else if (persisted.only_my_answer && this.currentDoctor) {
      this.doctorId = this.currentDoctor.id;
    } else {
      this.doctorId = null;
    }
    if (!this.isOperatorDoctor) {
      this.excludeMode = "without_exclude";
    } else if (persisted.exclude_mode) {
      this.excludeMode = persisted.exclude_mode;
    } else {
      this.excludeMode = "without_exclude";
    }
  },

  methods: {
    // --- 一括操作 ---
    onToggleBulkSelection({ id, checked }) {
      if (!checked) {
        this.selectedBulkIds = this.selectedBulkIds.filter((sid) => sid !== id);
        return;
      }
      if (!this.selectedBulkIds.includes(id)) {
        this.selectedBulkIds = [...this.selectedBulkIds, id];
      }
    },
    toggleAllBulkSelection(checked) {
      this.selectedBulkIds = checked
        ? (this.anytimeConsultations || []).slice(0, 100).map((c) => c.id)
        : [];
    },
    clearBulkSelection() {
      this.selectedBulkIds = [];
    },
    showToast({ message, color = "success" }) {
      this.snackbarMessage = message;
      this.snackbarColor = color;
      this.snackbar = true;
    },

    // --- 検索API呼び出し ---
    searchAnytimeConsultations(requestBase) {
      if (!requestBase) return Promise.resolve();

      this.isSearching = true;
      this.elasticsearchErrorDetail = "";

      return anytimeConsultationApi
        .answeredSearchAnytimeConsultations(requestBase)
        .then((response) => {
          const list = response.data || [];
          this.anytimeConsultations = list;
          this.totalCount = response.total_count || 0;
          this.excludedCount = response.excluded_count || 0;
          this.lastSearchRequestBase = { ...requestBase };
        })
        .catch((error) => {
          this.anytimeConsultations = [];
          this.totalCount = 0;
          this.excludedCount = 0;
          this.elasticsearchErrorDetail = error?.response?.data?.message || "ElasticSearch(検索エンジン)との接続に失敗しました";
          throw error;
        })
        .finally(() => {
          this.isSearching = false;
        });
    },

    // --- リスト操作 ---
    updateList() {
      const doctorFamilyName = this.doctorId != null && this.currentDoctor
        ? (this.currentDoctor.familyName || "")
        : this.doctorFamilyName;
      const doctorFirstName = this.doctorId != null && this.currentDoctor
        ? (this.currentDoctor.firstName || "")
        : this.doctorFirstName;

      this.previousKeys = buildSearchKeys({
        departmentTypes: this.departmentTypes,
        icd10Keyword: this.icd10Keyword,
        doctorFamilyName,
        doctorFirstName,
        splitKeywords: this.splitKeywords,
        hasPediatricDepartment: this.hasPediatricDepartment,
        childGenders: this.childGenders,
        childAgeSlider: this.childAgeSlider,
        doctorId: this.doctorId,
        isOperatorDoctor: this.isOperatorDoctor,
        excludeMode: this.excludeMode,
      });
      this.previousSearchQuery = buildAnsweredSearchRequestQuery(this.previousKeys);
      persistSearchKeys(this.previousKeys);
      this.searchResultSessionId += 1;
      this.selectedBulkIds = [];
      this.searchAnytimeConsultations(this.answeredSearchRequestBase);
    },
    refreshSearchResults(options = {}) {
      if (!this.previousKeys) return;

      const delayMs = Math.max(Number(options.delayMs) || 0, 0);
      this.searchResultSessionId += 1;
      this.selectedBulkIds = [];

      // 待機中でも即座にローディング表示へ切り替える
      this.isSearching = true;

      if (delayMs > 0) {
        setTimeout(() => {
          this.searchAnytimeConsultations(this.answeredSearchRequestBase);
        }, delayMs);
        return;
      }

      this.searchAnytimeConsultations(this.answeredSearchRequestBase);
    },

    // --- 表示ヘルパー（テンプレートから呼び出し）---
    formatDepartmentTypes,
  },
};
</script>

<style lang="scss">
.results {
  padding: 20px;

  .search-keys {
    border: solid 1px #000000;
  }

  .elasticsearch-error-detail {
    white-space: pre-line;
  }
}
</style>
